import { fetchData } from '@/utils/fetch'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/pinia/user'
import { v4 as uuidv4 } from 'uuid'
import { useCurrencyStore } from '@/pinia/currency'
import { useHistoryStore } from '@/pinia/history'
import { useShipStore } from '@/pinia/ship'
import { createSharedComposable } from '@vueuse/core'

export const useProductDetail = createSharedComposable(() => {
  const userStore = useUserStore()
  const route = useRoute()
  const router = useRouter()
  const currencyStore = useCurrencyStore()
  const { insert: insertHistory } = useHistoryStore()

  const ship = useShipStore()

  const detailInfo = ref(null)
  const showSkeleton = ref(false)
  const skuCartStatus = ref([])

  const swiperImages = ref([]) // 轮播图图片 = 商品主图 + sku图
  const productImages = ref([]) // 商品主图
  const styleImages = ref([]) // sku图
  const variantImages = ref([]) // 供PC图片预览组件使用
  const asyncDescriptionImages = ref([])

  const descriptionImages = computed(() => {
    if (detailInfo.value == null) return []
    const list = []
    const { sizeImage, descriptionList, productImage } = detailInfo.value
    if (sizeImage) list.push(sizeImage)
    if (descriptionList?.length > 0) list.push(...descriptionList)
    else if (asyncDescriptionImages.value?.length > 0) list.push(...asyncDescriptionImages.value)
    else list.push(...productImage.images)
    return list
  })

  const skuAttributes = ref([])

  const expandServices = ref([])

  const currencyData = computed(() => currencyStore.current)

  const getDetailInfo = async () => {
    showSkeleton.value = true

    const { code, data, msg } = await fetchData('/appb/b2b/product/searchProductDetail', {
      method: 'post',
      body: JSON.stringify({
        offerId: route.params.id
      })
    })

    if (code === 200) {
      detailInfo.value = data
      skuAttributes.value = formatAttributes(data.productSkuInfos)
      expandServices.value = formatAttributes(data.expandSkuInfos)

      insertHistory({
        offerId: data.offerId,
        imageUrl: data.productImage.images[0],
        priceInfo: {
          price: (data.productSkuInfos[0].priceRangeList[1] ?? data.productSkuInfos[0].priceRangeList[0]).price
        },
        subjectTrans: data.subjectTrans
      })
      if (userStore.isLogged) {
        getSkuCartStatus()
      }
      if (detailInfo.value.descriptionList == null) {
        getDescriptionImages()
      }
      getSourcingStatus()
      initSwiperImages(data)
      getSpecifications(data)
    }
    if (code === 500 || code === 5210) {
      if (msg === 'The product does not exist.') {
        router.push({
          name: 'invalidError',
          query: { reason: 'The product does not exist.' }
        })
      }
    }
    showSkeleton.value = false
  }

  const getSkuCartStatus = async () => {
    const params = {
      spuId: detailInfo.value.offerId,
      skuIds: detailInfo.value.productSkuInfos.map(item => item.skuId)
    }
    const { data } = await fetchData('/appb/b2b/cart/getPendingSkuBySpu', {
      method: 'post',
      body: JSON.stringify(params)
    })
    skuCartStatus.value = data || []
  }

  const initSwiperImages = (detailInfo) => {
    const productImage = detailInfo.productImage.images || []
    const productSkuInfos = detailInfo.productSkuInfos || []

    const firstAttributes = productSkuInfos.map(item => item.skuAttributes[0])

    // 去重处理
    const uniqueFirstAttributes = firstAttributes.filter((item, index, self) => {
      return index === self.findIndex(v => v.valueTrans === item.valueTrans)
    })

    const skuImageUrl = []
    const _variantImages = []
    uniqueFirstAttributes.forEach(item => {
      if (item.skuImageUrl) {
        skuImageUrl.push(item.skuImageUrl)
        _variantImages.push({
          attributeNameTrans: item.attributeNameTrans,
          skuImageUrl: item.skuImageUrl,
          valueTrans: item.valueTrans
        })
      }
    })

    styleImages.value = skuImageUrl
    productImages.value = productImage
    swiperImages.value = [...productImage, ...skuImageUrl]
    variantImages.value = _variantImages
  }

  const getSourcingStatus = async () => {
    const productSkuInfos = detailInfo.value.productSkuInfos
    const { code, data } = await fetchData('/appb/alibaba-common/offerDetail/sourcingStatusV2', {
      method: 'POST',
      body: JSON.stringify({
        offerId: detailInfo.value.offerId,
        skuIds: productSkuInfos.map(item => item.skuId)
      })
    })

    if (code === 200) {
      if (Number(data.status) === 3) {
        router.push({
          name: 'invalidError',
          query: { reason: data.message }
        })
      }
    }
  }

  const formatImageList = (detailInfo) => {
    const productSkuInfos = detailInfo.productSkuInfos

    const firstAttributes = productSkuInfos.map(item => {
      return {
        ...item.skuAttributes[0],
        skuId: item.skuId,
        quantity: 0,
        price: item.price,
        priceRangeList: item.priceRangeList,
        bundleQuantity: item.bundleQuantity,
        singleAttr: item.skuAttributes.length === 1
      }
    })
    // 去重处理
    const uniqueFirstAttributes = firstAttributes.filter((item, index, self) => {
      return index === self.findIndex(v => v.valueTrans === item.valueTrans)
    })
    return uniqueFirstAttributes
  }

  const formatAttributes = (skuInfos) => {
    const result = []

    // 是否单属性
    skuInfos.forEach(item => {
      const singleAttribute = item.skuAttributes.length === 1

      if (singleAttribute) {
        const valueTrans = item.skuAttributes[0].valueTrans
        result.push({
          tag: valueTrans,
          amountOnSale: item.amountOnSale,
          skuId: item.skuId,
          skuImageUrl: item.skuAttributes[0].skuImageUrl,
          bundleQuantity: item.bundleQuantity,
          price: item.price,
          priceRangeList: item.priceRangeList,
          canUseMoq: item.canUseMoq,
          quantity: 0,
          checked: false,
          singleAttr: true
        })
      } else {
        const valueTrans = item.skuAttributes[0].valueTrans
        const mapItem = {
          ...item.skuAttributes[1],
          tag: valueTrans,
          amountOnSale: item.amountOnSale,
          quantity: 0,
          skuId: item.skuId,
          skuImageUrl: item.skuAttributes[0].skuImageUrl,
          bundleQuantity: item.bundleQuantity,
          price: item.price,
          priceRangeList: item.priceRangeList,
          canUseMoq: item.canUseMoq,
          checked: false,
          singleAttr: false
        }
        result.push(mapItem)
      }
    })
    return result
  }

  const formatPriceRangeList = async (spuId, sku, categoryId) => {
    const _priceRangeList = sku.priceRangeList.map(item => {
      item.uniqueKey = uuidv4()
      return item
    })

    const params = {
      address: {
        country: ship.country.countryName,
        countryCode: ship.country.countryCode,
        zip: ship.postCode
      },
      currency: currencyData.value.targetCurrency,
      fillAllUnShip: true, // 详情页固定传true
      details: _priceRangeList.map(item => {
        return {
          spuId,
          skuId: sku.skuId,
          skuQuantity: item.startQuantity,
          categoryId,
          uniqueKey: item.uniqueKey
        }
      })
    }
    const { code, data } = await fetchData('/appb/b2b/fee/calcShipFee', {
      method: 'post',
      body: JSON.stringify(params)
    })

    if (code === 200) {
      const details = data.details
      const singleShipPackages = details[0].singleShipPackages // 数组

      let minDeliveryTime = singleShipPackages[0].minDeliveryTime
      let maxDeliveryTime = singleShipPackages[0].maxDeliveryTime

      singleShipPackages.forEach(item => {
        const uniqueKey = item.uniqueKeys[0]
        _priceRangeList.forEach(_item => {
          if (_item.uniqueKey === uniqueKey) {
            _item.shipAmount = +item.shipAmount
          }

          if (minDeliveryTime > item.minDeliveryTime) {
            minDeliveryTime = item.minDeliveryTime
          }
          if (maxDeliveryTime < item.maxDeliveryTime) {
            maxDeliveryTime = item.maxDeliveryTime
          }
        })
      })

      return {
        priceRangeList: _priceRangeList,
        minDeliveryTime,
        maxDeliveryTime
      }
    }
  }

  const getSpecifications = (detailInfo) => {
    const productAttribute = detailInfo.productAttribute
    const result = {}

    // 遍历输入数组
    productAttribute.forEach((item) => {
      const {
        attributeId,
        attributeName,
        attributeNameTrans,
        value,
        valueTrans
      } = item

      if (!result[attributeId]) {
        // 如果 attributeId 不在 result 对象中，初始化
        result[attributeId] = {
          attributeId,
          attributeName,
          attributeNameTrans,
          value,
          valueTrans
        }
      } else {
        // 如果 attributeId 已存在，拼接 valueTrans
        result[attributeId].valueTrans += `, ${valueTrans}`
      }
    })

    // 将结果对象转换为数组
    return Object.values(result)
  }

  const getDescriptionImages = async () => {
    const { code, data } = await fetchData(
      '/appb/alibaba-common/searchProductImages',
      {
        method: 'post',
        body: JSON.stringify({
          offerId: detailInfo.value.offerId
        })
      }
    )
    if (code === 200) {
      asyncDescriptionImages.value = data
    }
  }

  return {
    detailInfo,
    skuAttributes,
    expandServices,
    showSkeleton,
    skuCartStatus,
    swiperImages,
    productImages,
    styleImages,
    descriptionImages,
    variantImages,
    getDetailInfo,
    getSkuCartStatus,
    formatImageList,
    formatPriceRangeList,
    initSwiperImages,
    getSpecifications
  }
})
